import axios, { AxiosError, type AxiosProgressEvent } from 'axios';
import type { UploadAttachmentError, UploadAttachmentResponse } from '../types/attachments';
import { VITE_API_URL } from './env';

export type UploadProgressCallback = (percent: number) => void;

export interface UploadWithProgressOptions {
  /** API path beginning with `/`, e.g. `/attachments/upload/123` */
  path: string;
  file: File;
  token?: string | null;
  additionalData?: Record<string, string>;
  onProgress?: UploadProgressCallback;
  fieldName?: string;
}

/**
 * Converts an Axios upload progress event into a 0-100 integer percent.
 * Returns null when the total size is unknown.
 */
export const calculateUploadPercent = (event: AxiosProgressEvent): number | null => {
  if (!event.total || event.total <= 0) {
    return null;
  }

  const percent = Math.round((event.loaded / event.total) * 100);
  return Math.min(Math.max(percent, 0), 100);
};

/** Upload errors may carry the HTTP status for auth retry handling. */
export type UploadFailure = UploadAttachmentError & { status?: number };

export const getUploadFailureStatus = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null || !('status' in error)) {
    return undefined;
  }

  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
};

const toUploadError = (error: unknown): UploadFailure => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<UploadAttachmentError | { message?: string }>;
    const status = axiosError.response?.status;
    const data = axiosError.response?.data;

    if (data && typeof data === 'object') {
      return { ...(data as UploadAttachmentError), status };
    }

    return {
      error: 'UPLOAD_FAILED',
      message: axiosError.message || 'Upload failed. Please try again.',
      FormID: 0,
      status,
    };
  }

  if (
    typeof error === 'object' &&
    error !== null &&
    ('validationErrors' in error || 'message' in error)
  ) {
    return error as UploadFailure;
  }

  return {
    error: 'UPLOAD_FAILED',
    message: 'Upload failed. Please try again.',
    FormID: 0,
  };
};

/**
 * Uploads a multipart file with real browser upload progress events.
 * Caps reported progress at 99% until the server responds successfully, then reports 100%.
 */
export async function uploadWithProgress({
  path,
  file,
  token,
  additionalData,
  onProgress,
  fieldName = 'file',
}: UploadWithProgressOptions): Promise<UploadAttachmentResponse> {
  const formData = new FormData();
  formData.append(fieldName, file);

  if (additionalData) {
    Object.entries(additionalData).forEach(([key, value]) => {
      formData.append(key, value);
    });
  }

  const baseUrl = typeof VITE_API_URL === 'string' ? VITE_API_URL.replace(/\/$/, '') : '';
  const url = `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`;

  let lastReportedPercent = -1;

  const reportProgress = (percent: number) => {
    if (percent === lastReportedPercent) {
      return;
    }
    lastReportedPercent = percent;
    onProgress?.(percent);
  };

  try {
    const response = await axios.post<UploadAttachmentResponse>(url, formData, {
      headers: {
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      // Large files on slow networks must not be cut off by a short default timeout.
      timeout: 0,
      onUploadProgress: (event) => {
        const percent = calculateUploadPercent(event);
        if (percent == null) {
          return;
        }
        // Hold at 99 until the server accepts the upload.
        reportProgress(Math.min(percent, 99));
      },
    });

    reportProgress(100);
    return response.data;
  } catch (error) {
    throw toUploadError(error);
  }
}
